import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link as RLink } from "react-router-dom";
import { Focus } from "lucide-react";
import { GrupoAvatar } from "../../components/GrupoAvatar";
import { ArrowLeft, Send, Paperclip, Bot, Trash2, Edit2, X, Image as ImageIcon, Mic, Video, FileText, MoreVertical, Users, Copy, Check, CheckCheck, UploadCloud, Download, ZoomIn, Eraser, Reply, Search, ChevronUp, ChevronDown } from "lucide-react";
import { AudioWavePlayer } from "../../components/AudioWavePlayer";
import { Avatar } from "../../components/Avatar";
import {
  buscarConversa,
  listarMensagens,
  buscarMensagens,
  enviarMensagem,
  enviarComAnexo,
  marcarLido,
  removerMensagem,
  editarMensagem,
  avisarDigitando,
  limparHistoricoIa,
  ChatMensagem,
} from "../../services/chat";
import { listarPessoas } from "../../services/presence";
import { tituloConversa } from "./ChatPage";
import { useAuth } from "../../hooks/useAuth";
import { AudioRecorder } from "../../components/AudioRecorder";
import { anexoUrlAbsoluto } from "../../services/tarefas";
import { useConfirm } from "../../components/ConfirmProvider";
import { ParticipantesModal } from "./ParticipantesModal";
import { getAudioGravacaoAtivo, getEmojiPickerAtivo } from "../../hooks/useChatNotifications";
import { copiarTexto } from "../../utils/clipboard";
import { useEscape } from "../../hooks/useEscape";
import { renderTextoRico } from "../../utils/textoComLinks";
import { EmojiPicker } from "../../components/EmojiPicker";

export function ChatThread({ conversaId, onVoltar }: { conversaId: string; onVoltar: () => void }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const confirm = useConfirm();
  const [texto, setTexto] = useState("");
  const [editandoId, setEditandoId] = useState<string | null>(null);
  // Quando o usuário clica no ícone de reply de uma mensagem, guardamos um snapshot
  // suficiente pra mostrar o preview no composer (id real vai pro backend).
  const [respondendoA, setRespondendoA] = useState<{ id: string; preview: string; autor: string } | null>(null);
  const [showParticipantes, setShowParticipantes] = useState(false);
  const [arrastando, setArrastando] = useState(false);
  const [audioAtivo, setAudioAtivo] = useState(getAudioGravacaoAtivo());
  const [emojiAtivo, setEmojiAtivo] = useState(getEmojiPickerAtivo());
  const [digitando, setDigitando] = useState<{ usuarioId: string; nome: string; ate: number } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const composerRef = useRef<HTMLTextAreaElement>(null);
  // Âncora invisível no final da lista — alvo do scroll. Mais confiável que
  // calcular scrollHeight (que pode estar desatualizado entre commit do React
  // e mudanças tardias de layout, ex.: composer crescendo, balão com mídia).
  const bottomAnchorRef = useRef<HTMLDivElement>(null);
  const lastTypingSentRef = useRef<number>(0);

  // ESC cancela edição antes de "voltar pra lista"
  useEscape(!!editandoId, () => {
    setEditandoId(null);
    setTexto("");
  });
  // ESC também cancela o reply em curso
  useEscape(!!respondendoA && !editandoId, () => {
    setRespondendoA(null);
  });

  // Reage ao toggle de gravação no painel de config
  useEffect(() => {
    function onToggle(e: any) { setAudioAtivo(!!e.detail); }
    window.addEventListener("growsoft:chat-audio-toggle", onToggle);
    return () => window.removeEventListener("growsoft:chat-audio-toggle", onToggle);
  }, []);

  // Reage a toggle do picker de emoji no menu de config do chat
  useEffect(() => {
    function onEmoji(e: any) { setEmojiAtivo(!!e.detail); }
    window.addEventListener("growsoft:chat-emoji-toggle", onEmoji);
    return () => window.removeEventListener("growsoft:chat-emoji-toggle", onEmoji);
  }, []);

  const { data: conversa } = useQuery({
    queryKey: ["chat-conversa", conversaId],
    queryFn: () => buscarConversa(conversaId),
  });

  // Página "viva" — só as 50 mensagens MAIS RECENTES. É a única query que sofre
  // refetch (a cada 30s + ao enviar/receber). Não inclui o histórico paginado
  // pra cima (isso vive em `pagasAntigas` no state local).
  const PAGINA_TAMANHO = 50;
  const { data: pagaRecente = [] } = useQuery({
    queryKey: ["chat-mensagens", conversaId],
    queryFn: () => listarMensagens(conversaId, { limite: PAGINA_TAMANHO }),
    refetchInterval: 30000,
  });
  // Páginas antigas carregadas sob demanda (scroll perto do topo). Cada item é
  // um lote retornado pelo backend (já em ordem cronológica crescente).
  const [pagasAntigas, setPagasAntigas] = useState<ChatMensagem[][]>([]);
  const [carregandoAntigas, setCarregandoAntigas] = useState(false);
  const [esgotouAntigas, setEsgotouAntigas] = useState(false);

  // Reseta paginação ao trocar de conversa
  useEffect(() => {
    setPagasAntigas([]);
    setCarregandoAntigas(false);
    setEsgotouAntigas(false);
  }, [conversaId]);

  // === Busca de mensagens dentro da conversa ===
  const [buscaAberta, setBuscaAberta] = useState(false);
  const [buscaQuery, setBuscaQuery] = useState("");
  const [buscaResultados, setBuscaResultados] = useState<ChatMensagem[]>([]);
  const [buscaCarregando, setBuscaCarregando] = useState(false);
  const [buscaIndice, setBuscaIndice] = useState(0);
  const [mensagemDestacadaId, setMensagemDestacadaId] = useState<string | null>(null);
  const buscaInputRef = useRef<HTMLInputElement>(null);
  const buscaSeqRef = useRef(0); // pra descartar respostas tardias

  // Limpa busca ao trocar de conversa
  useEffect(() => {
    setBuscaAberta(false);
    setBuscaQuery("");
    setBuscaResultados([]);
    setBuscaIndice(0);
  }, [conversaId]);

  // Debounce de 300ms — dispara busca conforme digita
  useEffect(() => {
    const q = buscaQuery.trim();
    if (!buscaAberta || q.length < 1) {
      setBuscaResultados([]);
      setBuscaCarregando(false);
      return;
    }
    setBuscaCarregando(true);
    const seq = ++buscaSeqRef.current;
    const t = setTimeout(async () => {
      try {
        const ms = await buscarMensagens(conversaId, q, { limite: 50 });
        if (seq !== buscaSeqRef.current) return; // resposta atrasada
        setBuscaResultados(ms);
        setBuscaIndice(0);
      } catch {
        setBuscaResultados([]);
      } finally {
        if (seq === buscaSeqRef.current) setBuscaCarregando(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [buscaQuery, buscaAberta, conversaId]);

  // Foca o input quando a busca abre
  useEffect(() => {
    if (buscaAberta) requestAnimationFrame(() => buscaInputRef.current?.focus());
  }, [buscaAberta]);

  // Foca automático no PRIMEIRO resultado quando a busca retorna (mais recente).
  useEffect(() => {
    if (buscaResultados.length === 0) return;
    const alvo = buscaResultados[0];
    // Tenta focar; se não estiver renderizado ainda, próxima vez que rolar
    // o usuário vai usar os botões manualmente.
    setTimeout(() => focarResultado(alvo.id), 30);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buscaResultados]);

  // Foca um resultado: scroll até a mensagem + highlight temporário.
  // Se a mensagem não estiver na janela atual (paginação), retorna false.
  function focarResultado(id: string): boolean {
    const el = scrollRef.current?.querySelector<HTMLElement>(`[data-msg-id="${id}"]`);
    if (!el) return false;
    el.scrollIntoView({ block: "center", behavior: "smooth" });
    setMensagemDestacadaId(id);
    setTimeout(() => setMensagemDestacadaId((cur) => (cur === id ? null : cur)), 1800);
    return true;
  }

  function navegarBusca(direcao: 1 | -1) {
    if (buscaResultados.length === 0) return;
    // resultados vêm do mais RECENTE pro mais antigo. "anterior" (chevron up)
    // = mais antigo = +1 no índice. "próximo" (chevron down) = mais recente = -1.
    const novo = Math.max(0, Math.min(buscaResultados.length - 1, buscaIndice + direcao));
    setBuscaIndice(novo);
    const alvo = buscaResultados[novo];
    if (alvo) focarResultado(alvo.id);
  }

  // Mensagens visíveis = páginas antigas (na ordem em que foram carregadas) +
  // a página recente. O backend já entrega cada lote em ordem cronológica.
  const mensagens = useMemo<ChatMensagem[]>(() => {
    const flat = pagasAntigas.flat();
    // Dedup defensivo — se uma mensagem migrou do "recente" pra "antigas"
    // entre fetches, evita key duplicada.
    const seen = new Set<string>();
    const out: ChatMensagem[] = [];
    for (const m of [...flat, ...pagaRecente]) {
      if (seen.has(m.id)) continue;
      seen.add(m.id);
      out.push(m);
    }
    return out;
  }, [pagasAntigas, pagaRecente]);

  // Presença em tempo real (para status online no header DM)
  const { data: pessoas = [] } = useQuery({
    queryKey: ["presence"],
    queryFn: listarPessoas,
    refetchInterval: 30000,
  });

  // Listener do evento "digitando" — mostra "X digitando..." por 4s
  useEffect(() => {
    function onDig(e: any) {
      const detail = e.detail as { conversaId: string; usuarioId: string; nome: string };
      if (detail.conversaId !== conversaId) return;
      if (detail.usuarioId === user?.id) return;
      setDigitando({ usuarioId: detail.usuarioId, nome: detail.nome, ate: Date.now() + 4000 });
    }
    window.addEventListener("growsoft:chat.digitando", onDig);
    return () => window.removeEventListener("growsoft:chat.digitando", onDig);
  }, [conversaId, user?.id]);

  // Limpa indicador "digitando" passado
  useEffect(() => {
    if (!digitando) return;
    const t = setTimeout(() => {
      if (digitando && digitando.ate <= Date.now()) setDigitando(null);
    }, Math.max(50, digitando.ate - Date.now()));
    return () => clearTimeout(t);
  }, [digitando]);

  // Marca como lido sempre que abre/recebe mensagem nova. Depende de
  // `pagaRecente.length` pra não disparar request à toa quando o usuário só
  // carrega histórico antigo (paginação retroativa).
  // Atualização otimista no cache do lado do front: a UI da lista de conversas
  // zera o contador IMEDIATAMENTE, sem esperar refetch (era essa a "lentidão"
  // do badge demorar a sumir).
  useEffect(() => {
    if (!conversaId) return;
    // Otimista: zera naoLidas/marcadoNaoLido da conversa atual no cache
    qc.setQueryData<any[]>(["chat-conversas"], (old) => {
      if (!Array.isArray(old)) return old;
      let mudou = false;
      const novo = old.map((c) => {
        if (c.id !== conversaId) return c;
        if (!c.naoLidas && !c.marcadoNaoLido) return c;
        mudou = true;
        return { ...c, naoLidas: 0, marcadoNaoLido: false };
      });
      return mudou ? novo : old;
    });
    marcarLido(conversaId)
      .then(() => {
        // Sincroniza com o servidor (caso `ultimaLeituraEm` afete outras coisas)
        qc.invalidateQueries({ queryKey: ["chat-conversas"] });
      })
      .catch(() => null);
  }, [conversaId, pagaRecente.length, qc]);

  const limparIaM = useMutation({
    mutationFn: () => limparHistoricoIa(conversaId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["chat-mensagens", conversaId] });
      qc.invalidateQueries({ queryKey: ["chat-conversas"] });
    },
  });

  async function aoLimparIa() {
    const ok = await confirm({
      title: "Limpar histórico da IA?",
      message: "Todas as mensagens desta conversa com a IA serão apagadas para liberar a janela de contexto. Essa ação não pode ser desfeita.",
      confirmText: "Limpar",
      danger: true,
    });
    if (ok) limparIaM.mutate();
  }

  // Comportamento simplificado a pedido do usuário: SEMPRE rola pro fim — tanto
  // ao abrir a conversa quanto a cada mensagem nova (enviada ou recebida). O
  // salto pra "primeira não-lida" foi desativado por enquanto.
  function rolarParaFim(behavior: ScrollBehavior = "auto") {
    // scrollTop = scrollHeight no PRÓPRIO container é mais confiável que
    // scrollIntoView (que pode rolar o document/pai em vez do scroll
    // interno em alguns layouts). Anchor é só pra debug/fallback.
    const el = scrollRef.current;
    if (!el) return;
    if (behavior === "smooth") {
      el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
    } else {
      el.scrollTop = el.scrollHeight;
    }
  }

  // Detecção "scroll perto do topo" → carrega próxima página de mensagens
  // antigas. Preserva a posição visual: mede scrollHeight antes do fetch e
  // ajusta scrollTop depois pro usuário continuar no mesmo ponto.
  async function carregarAntigas() {
    if (carregandoAntigas || esgotouAntigas) return;
    const todas = mensagens;
    if (todas.length === 0) return;
    const maisAntiga = todas[0];
    const el = scrollRef.current;
    const alturaAntes = el?.scrollHeight ?? 0;
    const topoAntes = el?.scrollTop ?? 0;

    setCarregandoAntigas(true);
    try {
      const lote = await listarMensagens(conversaId, {
        antesDe: maisAntiga.criadoEm,
        limite: PAGINA_TAMANHO,
      });
      if (lote.length === 0) {
        setEsgotouAntigas(true);
      } else {
        setPagasAntigas((arr) => [lote, ...arr]);
        if (lote.length < PAGINA_TAMANHO) setEsgotouAntigas(true);
        // Após o paint da página nova no topo, ajusta scrollTop pra preservar
        // a posição visual atual (evita pular pro fim ou pro novo topo).
        requestAnimationFrame(() => {
          const el2 = scrollRef.current;
          if (!el2) return;
          const delta = el2.scrollHeight - alturaAntes;
          el2.scrollTop = topoAntes + delta;
        });
      }
    } catch {
      // silencioso — usuário pode tentar rolar de novo
    } finally {
      setCarregandoAntigas(false);
    }
  }

  // Liga handler de scroll do container — quando próximo do topo, dispara carga.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    function onScroll() {
      if (!el) return;
      if (el.scrollTop < 120) {
        // Dispara só quando não estamos no zero absoluto da carga inicial.
        if (mensagens.length > 0) void carregarAntigas();
      }
    }
    el.addEventListener("scroll", onScroll);
    return () => el.removeEventListener("scroll", onScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversaId, carregandoAntigas, esgotouAntigas, mensagens.length]);

  // Auto-focus no campo de digitar ao abrir/trocar conversa.
  // Em mobile, abrir o teclado automaticamente é intrusivo (esconde a lista de
  // mensagens) — então só foca em telas grandes (≥640px) e quando não há
  // edição em curso (a edição já controla o foco no próprio fluxo).
  useEffect(() => {
    if (!conversaId || editandoId) return;
    if (typeof window === "undefined") return;
    const ehDesktop = window.matchMedia("(min-width: 640px)").matches;
    if (!ehDesktop) return;
    // Pequeno delay pra garantir que o textarea já montou na DOM.
    const t = setTimeout(() => composerRef.current?.focus(), 80);
    return () => clearTimeout(t);
  }, [conversaId, editandoId]);

  // Único efeito de scroll. Cobre 3 cenários:
  //  1. Abrir/trocar conversa (pagaRecente carrega de 0 → N) — pula pro fim instantâneo.
  //  2. Chega mensagem nova (length cresceu) — rola suave pro fim.
  //  3. Carregar histórico antigo (length cai nesse efeito não conta) — NÃO rola.
  // Não usa `mensagens.length` (que inclui pagasAntigas); usa SÓ `pagaRecente.length`.
  const ultimoScrollRef = useRef<{ conversaId: string; count: number } | null>(null);
  useEffect(() => {
    if (!pagaRecente.length) {
      ultimoScrollRef.current = { conversaId, count: 0 };
      return;
    }
    const prev = ultimoScrollRef.current;
    const ehMesmaConversa = prev?.conversaId === conversaId;
    // "Nova mensagem" só é smooth se já havia >=1 mensagem antes na mesma
    // conversa. Carga inicial (count 0 → N) usa auto pra evitar animação enorme.
    const ehNovaMsg = ehMesmaConversa && prev && prev.count > 0 && pagaRecente.length > prev.count;
    ultimoScrollRef.current = { conversaId, count: pagaRecente.length };
    const behavior: ScrollBehavior = ehNovaMsg ? "smooth" : "auto";

    // Vários ticks pra absorver layout shift (avatares, imagens carregando, etc).
    rolarParaFim(behavior);
    const t1 = requestAnimationFrame(() => rolarParaFim(behavior));
    const t2 = setTimeout(() => rolarParaFim("auto"), 200);
    const t3 = setTimeout(() => rolarParaFim("auto"), 500);
    const t4 = setTimeout(() => rolarParaFim("auto"), 1000);
    return () => {
      cancelAnimationFrame(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      clearTimeout(t4);
    };
  }, [pagaRecente.length, conversaId]);

  // Reseta a altura do textarea ao tamanho de 1 linha. O auto-grow só roda
  // no onChange (input do user); ao limpar via setTexto("") o elemento
  // continuava esticado. Chamar isto após cada envio.
  const resetarAlturaComposer = () => {
    if (composerRef.current) composerRef.current.style.height = "38px";
  };

  const enviarM = useMutation({
    mutationFn: () => enviarMensagem(conversaId, texto.trim(), respondendoA?.id),
    onSuccess: () => {
      setTexto("");
      setRespondendoA(null);
      resetarAlturaComposer();
      qc.invalidateQueries({ queryKey: ["chat-mensagens", conversaId] });
      qc.invalidateQueries({ queryKey: ["chat-conversas"] });
      // Reforço imediato pro envio próprio — não depende do refetch terminar.
      requestAnimationFrame(() => rolarParaFim("smooth"));
      setTimeout(() => rolarParaFim("auto"), 400);
    },
  });

  const editarM = useMutation({
    mutationFn: ({ id, novoTexto }: { id: string; novoTexto: string }) => editarMensagem(id, novoTexto),
    onSuccess: () => {
      setEditandoId(null);
      setTexto("");
      resetarAlturaComposer();
      qc.invalidateQueries({ queryKey: ["chat-mensagens", conversaId] });
    },
  });

  const removerM = useMutation({
    mutationFn: (id: string) => removerMensagem(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["chat-mensagens", conversaId] }),
  });

  const uploadM = useMutation({
    mutationFn: (file: File | Blob) => enviarComAnexo(conversaId, file, {
      texto: texto.trim() || undefined,
      respondendoA: respondendoA?.id,
    }),
    onSuccess: () => {
      setTexto("");
      setRespondendoA(null);
      resetarAlturaComposer();
      qc.invalidateQueries({ queryKey: ["chat-mensagens", conversaId] });
      qc.invalidateQueries({ queryKey: ["chat-conversas"] });
      // Anexo cresce de altura depois (preview carrega); reforça com delay maior.
      requestAnimationFrame(() => rolarParaFim("smooth"));
      setTimeout(() => rolarParaFim("auto"), 400);
      setTimeout(() => rolarParaFim("auto"), 900);
    },
    onError: (e: any) => {
      // Backend pode rejeitar (ex.: GIF bloqueado no chat). Mostra a mensagem
      // amigável que vem em `error.message` em vez do erro técnico do axios.
      const msg = e?.response?.data?.error?.message || "Falha ao enviar anexo.";
      alert(msg);
    },
  });

  function aoResponder(m: ChatMensagem) {
    const autor = m.autorIa ? "IA" : (m.autor?.nome || "alguém");
    const preview = (m.texto || "").trim().slice(0, 80) || (m.anexos.length > 0 ? "(anexo)" : "(mensagem)");
    setRespondendoA({ id: m.id, preview, autor });
    // Foca o composer pro usuário já começar a digitar (cursor visível, sem
    // precisar clicar). Em mobile, isso também abre o teclado. rAF + setTimeout
    // garantem que o textarea já está montado e visível depois do paint do
    // preview de resposta.
    requestAnimationFrame(() => {
      const el = composerRef.current;
      if (!el) return;
      el.focus();
      // Move cursor pro fim (caso já tenha texto digitado)
      const fim = el.value.length;
      try { el.setSelectionRange(fim, fim); } catch {}
    });
    setTimeout(() => composerRef.current?.focus(), 80);
  }

  // Paste de imagem (Ctrl+V) — captura no documento enquanto a thread está aberta
  useEffect(() => {
    function onPaste(e: ClipboardEvent) {
      if (!e.clipboardData) return;
      const arquivos: File[] = [];
      // Modo 1: clipboardData.items (Chrome/Firefox/Edge para imagens recortadas)
      if (e.clipboardData.items) {
        for (const it of Array.from(e.clipboardData.items)) {
          if (it.kind === "file") {
            const f = it.getAsFile();
            if (f) arquivos.push(f);
          }
        }
      }
      // Modo 2: fallback clipboardData.files (Safari, alguns casos)
      if (arquivos.length === 0 && e.clipboardData.files && e.clipboardData.files.length > 0) {
        for (const f of Array.from(e.clipboardData.files)) {
          arquivos.push(f);
        }
      }
      if (arquivos.length > 0) {
        e.preventDefault();
        e.stopPropagation();
        for (const f of arquivos) {
          // Renomeia se for nome genérico de imagem colada
          if (!f.name || f.name === "image.png" || f.name === "blob") {
            const ts = new Date().toISOString().replace(/[:.]/g, "-");
            const ext = f.type?.split("/")[1] || "png";
            const renamed = new File([f], `colado-${ts}.${ext}`, { type: f.type });
            uploadM.mutate(renamed);
          } else {
            uploadM.mutate(f);
          }
        }
      }
    }
    // window cobre mais cenários que document
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  }, [conversaId]);

  // Drag & drop dentro da thread
  function onDragEnter(e: React.DragEvent) {
    if (e.dataTransfer?.types?.includes("Files")) {
      e.preventDefault();
      setArrastando(true);
    }
  }
  function onDragOver(e: React.DragEvent) {
    if (e.dataTransfer?.types?.includes("Files")) e.preventDefault();
  }
  function onDragLeave(e: React.DragEvent) {
    if (e.target === wrapRef.current) setArrastando(false);
  }
  function onDrop(e: React.DragEvent) {
    e.preventDefault();
    setArrastando(false);
    const files = Array.from(e.dataTransfer.files);
    for (const f of files) uploadM.mutate(f);
  }

  function submit() {
    if (uploadM.isPending || enviarM.isPending) return;
    if (editandoId) {
      if (texto.trim()) editarM.mutate({ id: editandoId, novoTexto: texto.trim() });
      return;
    }
    if (!texto.trim()) return;
    enviarM.mutate();
  }

  if (!conversa) return <div className="flex-1 flex items-center justify-center text-slate-400">Carregando…</div>;

  const titulo = tituloConversa(conversa as any, user?.id);

  // Para DM, descobre o outro participante e seu status online
  const outroDM = conversa.tipo === "dm" ? conversa.participantes.find((p) => p.usuarioId !== user?.id) : null;
  const outroOnline = outroDM ? !!pessoas.find((p) => p.id === outroDM.usuarioId)?.online : false;

  const indicadorDigitando = digitando && Date.now() < digitando.ate
    ? (conversa.tipo === "dm" ? "digitando…" : `${digitando.nome.split(" ")[0]} está digitando…`)
    : null;

  const subt = indicadorDigitando
    ? indicadorDigitando
    : conversa.tipo === "ia"
      ? "Sempre online · responde tudo"
      : conversa.tipo === "grupo"
        ? `${conversa.participantes.length} membros`
        : (outroOnline ? "online" : "offline");

  return (
    <div
      ref={wrapRef}
      className="chat-scope flex flex-1 flex-col min-w-0 min-h-0 relative"
      onDragEnter={onDragEnter}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      {arrastando && (
        <div className="absolute inset-0 z-30 bg-blue-500/10 border-4 border-dashed border-blue-500 flex items-center justify-center pointer-events-none">
          <div className="bg-white rounded-xl px-6 py-4 shadow-xl text-center">
            <UploadCloud size={32} className="mx-auto mb-2 text-blue-600" />
            <p className="text-sm font-medium text-slate-800">Solte para enviar</p>
          </div>
        </div>
      )}
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-3 sm:px-5 py-3 flex items-center gap-2 shrink-0">
        <button onClick={onVoltar} className="md:hidden p-2 rounded hover:bg-slate-100" aria-label="Voltar">
          <ArrowLeft size={18} className="text-slate-600" />
        </button>
        {conversa.tipo === "dm" && outroDM && (
          <div className="relative shrink-0">
            <Avatar
              nome={outroDM.usuario.nome}
              fotoUrl={(outroDM.usuario as any).fotoUrl}
              tamanho={32}
              bgFallback="bg-blue-100"
              textFallback="text-blue-700"
            />
            <span
              className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-white ${outroOnline ? "bg-emerald-500" : "bg-slate-300"}`}
              title={outroOnline ? "Online" : "Offline"}
            />
          </div>
        )}
        {conversa.tipo === "grupo" && (
          <GrupoAvatar fotoUrl={conversa.fotoUrl} tamanho={32} titulo={conversa.nome || undefined} />
        )}
        <div className="flex-1 min-w-0">
          <p className="text-base font-semibold text-slate-800 truncate">{titulo}</p>
          <p className={`text-[11px] truncate ${
            indicadorDigitando ? "text-blue-600 italic" :
            (conversa.tipo === "dm" && outroOnline) ? "text-emerald-600" : "text-slate-500"
          }`}>
            {subt}
          </p>
        </div>
        {/* Pílula do OUTRO usuário (em DM) — mostra no que ele está focando agora.
            Útil pra saber se a pessoa está livre/ocupada antes de mandar mensagem.
            Backend já avalia se O USUÁRIO LOGADO pode abrir essa tarefa
            (`tarefaAtual.podeVer`). Se pode → renderiza como link clicável (drawer
            abre). Se não → renderiza como texto informativo (cinza, cursor padrão). */}
        {conversa.tipo === "dm" && outroDM?.usuario.tarefaAtual && (() => {
          const ta = outroDM.usuario.tarefaAtual;
          const classesBase = "hidden sm:inline-flex items-center gap-1 text-[10px] font-medium border px-1.5 py-0.5 rounded-full max-w-[140px] shrink";
          if (ta.podeVer) {
            return (
              <RLink
                to={`/tarefas?tarefa=${ta.id}`}
                className={`${classesBase} bg-blue-50 hover:bg-blue-100 dark:bg-blue-900/30 dark:hover:bg-blue-900/50 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-700`}
                title={`${outroDM.usuario.nome} está em: ${ta.titulo}`}
              >
                <Focus size={10} className="shrink-0" />
                <span className="truncate">{ta.titulo}</span>
              </RLink>
            );
          }
          return (
            <span
              className={`${classesBase} bg-slate-50 dark:bg-slate-800 text-slate-500 dark:text-slate-400 border-slate-200 dark:border-slate-700 cursor-default`}
              title={`${outroDM.usuario.nome} está em uma tarefa que você não tem acesso pra abrir.`}
            >
              <Focus size={10} className="shrink-0" />
              <span className="truncate">{ta.titulo}</span>
            </span>
          );
        })()}
        <button
          onClick={() => setBuscaAberta((v) => !v)}
          className={`p-2 rounded hover:bg-slate-100 dark:hover:bg-slate-700 ${buscaAberta ? "text-blue-600 bg-blue-50 dark:bg-blue-900/30" : "text-slate-600 dark:text-slate-300"}`}
          title="Buscar mensagens"
          aria-label="Buscar mensagens"
        >
          <Search size={16} />
        </button>
        {conversa.tipo === "ia" && (
          <button
            onClick={aoLimparIa}
            disabled={limparIaM.isPending}
            className="p-2 rounded hover:bg-slate-100 text-slate-600 disabled:opacity-50"
            title="Limpar histórico (libera contexto da IA)"
          >
            <Eraser size={16} />
          </button>
        )}
        {conversa.tipo !== "dm" && conversa.tipo !== "ia" && (
          <button
            onClick={() => setShowParticipantes(true)}
            className="p-2 rounded hover:bg-slate-100 text-slate-600"
            title="Participantes"
          >
            <Users size={16} />
          </button>
        )}
      </div>

      {/* Barra de busca de mensagens (expansível) */}
      {buscaAberta && (
        <div className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 px-3 sm:px-5 py-2 flex items-center gap-2 shrink-0">
          <Search size={14} className="text-slate-400 shrink-0" />
          <input
            ref={buscaInputRef}
            type="text"
            value={buscaQuery}
            onChange={(e) => setBuscaQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") { setBuscaAberta(false); return; }
              if (e.key === "Enter") {
                e.preventDefault();
                if (buscaResultados.length > 0) {
                  // Enter sem shift → próximo (mais recente). Shift+Enter → anterior.
                  navegarBusca(e.shiftKey ? 1 : -1);
                }
              }
            }}
            placeholder="Buscar nesta conversa…"
            className="flex-1 px-2 py-1 text-sm rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 outline-none focus:border-blue-500 min-w-0"
          />
          <span className="text-[11px] text-slate-500 dark:text-slate-400 tabular-nums shrink-0 whitespace-nowrap">
            {buscaCarregando
              ? "buscando…"
              : buscaResultados.length === 0
                ? (buscaQuery.trim() ? "nenhum" : "")
                : `${buscaIndice + 1}/${buscaResultados.length}`}
          </span>
          <button
            type="button"
            onClick={() => navegarBusca(1)}
            disabled={buscaResultados.length === 0 || buscaIndice >= buscaResultados.length - 1}
            className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300 disabled:opacity-30"
            title="Resultado mais antigo"
          >
            <ChevronUp size={14} />
          </button>
          <button
            type="button"
            onClick={() => navegarBusca(-1)}
            disabled={buscaResultados.length === 0 || buscaIndice <= 0}
            className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300 disabled:opacity-30"
            title="Resultado mais recente"
          >
            <ChevronDown size={14} />
          </button>
          <button
            type="button"
            onClick={() => setBuscaAberta(false)}
            className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500"
            title="Fechar (Esc)"
          >
            <X size={14} />
          </button>
        </div>
      )}

      {/* Mensagens */}
      <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto bg-slate-50">
        <div className="max-w-3xl mx-auto p-3 sm:p-4 space-y-2">
          {/* Indicador de paginação retroativa */}
          {(carregandoAntigas || (!esgotouAntigas && mensagens.length >= PAGINA_TAMANHO)) && (
            <div className="text-center py-2">
              <span className="inline-block text-[11px] text-slate-500 dark:text-slate-400 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-full px-3 py-1">
                {carregandoAntigas ? "Carregando mensagens antigas…" : esgotouAntigas ? "" : "Role para cima pra carregar mais"}
              </span>
            </div>
          )}
          {esgotouAntigas && mensagens.length > PAGINA_TAMANHO && (
            <div className="text-center py-2">
              <span className="text-[10px] uppercase tracking-wide text-slate-400">Início da conversa</span>
            </div>
          )}
          {mensagens.length === 0 && (
            <div className="text-center text-sm text-slate-400 py-12">
              Nenhuma mensagem ainda. Mande a primeira! Mencione <code className="bg-slate-200 px-1 rounded">@ia</code> para chamar a assistente.
            </div>
          )}
          {mensagens.map((m, i) => {
            const anterior = mensagens[i - 1];
            const mostrarRotuloAutor = !anterior || anterior.autorId !== m.autorId || anterior.autorIa !== m.autorIa;
            const ehMinha = !m.autorIa && m.autorId === user?.id;
            // Vistinhos só pra DM em mensagens próprias
            let visto: "enviada" | "vista" | null = null;
            if (ehMinha && conversa.tipo === "dm" && outroDM) {
              const ultLeit = outroDM.ultimaLeituraEm ? new Date(outroDM.ultimaLeituraEm).getTime() : 0;
              visto = new Date(m.criadoEm).getTime() <= ultLeit ? "vista" : "enviada";
            }
            return (
              <div
                key={m.id}
                data-msg-id={m.id}
                className={`transition-shadow rounded-lg ${
                  mensagemDestacadaId === m.id ? "ring-2 ring-yellow-300 ring-offset-2 ring-offset-slate-50 dark:ring-offset-slate-900" : ""
                }`}
              >
                <MensagemBubble
                  m={m}
                  ehMinha={ehMinha}
                  mostrarRotuloAutor={mostrarRotuloAutor && conversa.tipo !== "dm"}
                  visto={visto}
                  onRemover={async () => {
                    const ok = await confirm({ title: "Remover mensagem", message: "Tem certeza?", danger: true, confirmText: "Remover" });
                    if (ok) removerM.mutate(m.id);
                  }}
                  onEditar={() => { setEditandoId(m.id); setTexto(m.texto || ""); }}
                  onResponder={() => aoResponder(m)}
                  onIrParaResposta={(id) => focarResultado(id)}
                />
              </div>
            );
          })}

          {/* Indicador "digitando" no fim da lista (DM/grupo, exceto IA) */}
          {indicadorDigitando && conversa.tipo !== "ia" && (
            <div className="flex justify-start">
              <div className="bg-white border border-slate-200 rounded-2xl rounded-bl-sm px-3 py-2 dark:bg-slate-800">
                <div className="flex items-center gap-1">
                  <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce [animation-delay:-0.3s]" />
                  <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce [animation-delay:-0.15s]" />
                  <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" />
                </div>
              </div>
            </div>
          )}
          {/* Âncora pro auto-scroll; pt-1 garante 4px de respiro pro último balão. */}
          <div ref={bottomAnchorRef} className="pt-1" aria-hidden="true" />
        </div>
      </div>

      {/* Composer */}
      <div className="border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shrink-0 sticky bottom-0 z-10">
        <div className="max-w-3xl mx-auto p-2 sm:p-3">
          {editandoId && (
            <div className="flex items-center gap-2 mb-2 text-xs bg-amber-50 text-amber-700 px-3 py-1.5 rounded">
              <Edit2 size={12} /> Editando mensagem
              <button onClick={() => { setEditandoId(null); setTexto(""); }} className="ml-auto"><X size={12} /></button>
            </div>
          )}
          {respondendoA && !editandoId && (
            <div className="flex items-start gap-2 mb-2 text-xs bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-200 px-3 py-1.5 rounded border-l-2 border-blue-400">
              <Reply size={12} className="mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">Respondendo a {respondendoA.autor}</p>
                <p className="text-[11px] opacity-80 truncate">{respondendoA.preview}</p>
              </div>
              <button onClick={() => setRespondendoA(null)} title="Cancelar resposta (Esc)" className="shrink-0 mt-0.5">
                <X size={12} />
              </button>
            </div>
          )}
          <div className="flex items-end gap-2">
            <button
              onClick={() => fileRef.current?.click()}
              disabled={!!editandoId}
              className="p-2 rounded-lg hover:bg-slate-100 text-slate-600 disabled:opacity-30"
              title="Anexar arquivo"
            >
              <Paperclip size={18} />
            </button>
            <input
              ref={fileRef}
              type="file"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) uploadM.mutate(f);
                if (fileRef.current) fileRef.current.value = "";
              }}
            />
            {emojiAtivo && <EmojiPicker
              disabled={uploadM.isPending}
              onSelect={(emoji) => {
                const el = composerRef.current;
                if (!el) {
                  setTexto((t) => t + emoji);
                  return;
                }
                // Insere no cursor (preservando posição), foca de volta.
                const start = el.selectionStart ?? el.value.length;
                const end = el.selectionEnd ?? el.value.length;
                const novo = el.value.slice(0, start) + emoji + el.value.slice(end);
                setTexto(novo);
                requestAnimationFrame(() => {
                  if (!composerRef.current) return;
                  composerRef.current.focus();
                  const pos = start + emoji.length;
                  composerRef.current.setSelectionRange(pos, pos);
                });
              }}
            />}
            {!editandoId && audioAtivo && (
              <AudioRecorder onReady={(blob, nome) => uploadM.mutate(new File([blob], nome, { type: blob.type }))} />
            )}

            <textarea
              ref={composerRef}
              value={texto}
              onChange={(e) => {
                setTexto(e.target.value);
                // auto-grow
                const el = e.target as HTMLTextAreaElement;
                el.style.height = "auto";
                el.style.height = Math.min(el.scrollHeight, 120) + "px";
                // Avisa "estou digitando" no máximo a cada 2s
                if (e.target.value.trim()) {
                  const agora = Date.now();
                  if (agora - lastTypingSentRef.current > 2000) {
                    lastTypingSentRef.current = agora;
                    avisarDigitando(conversaId).catch(() => null);
                  }
                }
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  submit();
                }
              }}
              rows={1}
              placeholder={editandoId ? "Editar mensagem…" : "Digite aqui..."}
              className="flex-1 resize-none px-3 py-2 rounded-2xl border border-slate-300 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 text-sm outline-none focus:border-blue-500 leading-snug [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
              disabled={uploadM.isPending}
              style={{ height: "38px", maxHeight: "120px" }}
            />
            <button
              onClick={submit}
              disabled={!texto.trim() || enviarM.isPending || editarM.isPending || uploadM.isPending}
              className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg p-2 disabled:opacity-40"
              aria-label="Enviar"
            >
              <Send size={18} />
            </button>
          </div>
          {uploadM.isPending && <p className="text-xs text-slate-500 mt-1">Enviando arquivo…</p>}
        </div>
      </div>

      {showParticipantes && conversa && (
        <ParticipantesModal conversa={conversa as any} onClose={() => setShowParticipantes(false)} />
      )}
    </div>
  );
}

function MensagemBubble({ m, ehMinha, mostrarRotuloAutor, visto, onRemover, onEditar, onResponder, onIrParaResposta }: {
  m: ChatMensagem;
  ehMinha: boolean;
  mostrarRotuloAutor: boolean;
  visto: "enviada" | "vista" | null;
  onRemover: () => void;
  onEditar: () => void;
  onResponder: () => void;
  onIrParaResposta: (id: string) => boolean;
}) {
  const [hover, setHover] = useState(false);
  const [copiado, setCopiado] = useState(false);
  const [expandido, setExpandido] = useState(false);
  // Texto longo + anexo deixava o balão gigante. Clampamos em ~5 linhas e oferecemos
  // expandir. Aplicamos só quando há anexo: bubble só de texto não polui a UI tanto.
  const podeTruncar = !!m.texto && m.anexos.length > 0 && m.texto.length > 200;
  if (m.removidoEm) {
    return (
      <div className={`flex ${ehMinha ? "justify-end" : "justify-start"}`}>
        <div className="text-xs italic text-slate-400 bg-slate-100 rounded-full px-3 py-1">mensagem removida</div>
      </div>
    );
  }

  const autorLabel = m.autorIa ? "IA" : (m.autor?.nome || "alguém");

  return (
    <div
      className={`flex ${ehMinha ? "justify-end" : "justify-start"} group`}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <div className={`max-w-[85%] ${ehMinha ? "items-end" : "items-start"} flex flex-col`}>
        {mostrarRotuloAutor && !ehMinha && (
          <span className="text-[10px] text-slate-500 mb-0.5 px-1">{autorLabel}</span>
        )}
        <div
          className={`relative rounded-2xl px-3 py-2 ${
            ehMinha
              ? "bg-blue-600 text-white rounded-br-sm"
              : m.autorIa
                ? "bg-gradient-to-br from-blue-50 to-blue-100 text-slate-800 border border-blue-200 rounded-bl-sm dark:!from-slate-700 dark:!to-slate-700 dark:!text-slate-100 dark:!border-blue-500/40"
                : "bg-white text-slate-800 border border-slate-200 rounded-bl-sm dark:!bg-slate-800 dark:!text-slate-100"
          }`}
        >
          {m.resposta && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                const id = m.resposta!.id;
                const ok = onIrParaResposta(id);
                if (!ok) {
                  // Mensagem fora da janela carregada — alerta sutil. Idealmente
                  // dispararíamos paginação retroativa até encontrar; ficou pra depois.
                  console.warn("Mensagem original fora da janela carregada — role pra cima pra carregar histórico.");
                }
              }}
              className={`text-[11px] mb-1 px-2 py-1 rounded border-l-2 w-full text-left cursor-pointer hover:brightness-110 transition ${
                ehMinha
                  ? "border-blue-300 bg-blue-700/30 text-blue-100"
                  : "border-slate-300 bg-slate-50 text-slate-500 hover:bg-slate-100 dark:bg-slate-700/50 dark:hover:bg-slate-700"
              }`}
              title="Ir para a mensagem citada"
            >
              <strong>{m.resposta.autor?.nome || "alguém"}:</strong> {m.resposta.texto?.slice(0, 80) || "(anexo)"}
            </button>
          )}
          {m.texto && (
            <>
              <p
                className={`text-sm whitespace-pre-wrap break-words [overflow-wrap:anywhere] ${
                  podeTruncar && !expandido ? "line-clamp-5" : ""
                }`}
              >
                {renderTextoRico(
                  m.texto,
                  ehMinha
                    ? "underline decoration-blue-200 underline-offset-2 text-white hover:decoration-white"
                    : "underline decoration-blue-400 underline-offset-2 text-blue-700 hover:text-blue-800",
                )}
              </p>
              {podeTruncar && (
                <button
                  type="button"
                  onClick={() => setExpandido((v) => !v)}
                  className={`text-[11px] font-medium mt-0.5 ${
                    ehMinha ? "text-blue-100 hover:text-white" : "text-blue-600 hover:text-blue-700"
                  }`}
                >
                  {expandido ? "ver menos" : "ver mais"}
                </button>
              )}
            </>
          )}
          {m.anexos.length > 0 && (
            <div className="space-y-1 mt-1">
              {m.anexos.map((a) => <AnexoChat key={a.id} a={a} clarinho={!ehMinha} />)}
            </div>
          )}
          <div className="flex items-center gap-1 mt-0.5 text-[10px] opacity-70">
            <span>{new Date(m.criadoEm).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}</span>
            {m.editadoEm && <span>· editada</span>}
            <button
              onClick={onResponder}
              className={`ml-auto p-0.5 rounded transition-colors ${ehMinha ? "hover:bg-blue-700" : "hover:bg-slate-100 dark:hover:bg-slate-700"}`}
              title="Responder"
            >
              <Reply size={12} />
            </button>
            {m.texto && (
              <button
                onClick={async () => {
                  const ok = await copiarTexto(m.texto || "");
                  if (ok) {
                    setCopiado(true);
                    setTimeout(() => setCopiado(false), 1200);
                  }
                }}
                className={`p-0.5 rounded transition-colors ${ehMinha ? "hover:bg-blue-700" : "hover:bg-slate-100 dark:hover:bg-slate-700"}`}
                title="Copiar mensagem"
              >
                {copiado ? <Check size={12} className="text-emerald-400" /> : <Copy size={12} />}
              </button>
            )}
            {visto && (
              <span className="flex items-center" title={visto === "vista" ? "Visualizada" : "Enviada"}>
                {visto === "vista"
                  ? <CheckCheck size={14} className="text-sky-400" strokeWidth={2.5} />
                  : <Check size={14} className="text-blue-200" strokeWidth={2.5} />}
              </span>
            )}
          </div>

          {hover && ehMinha && !m.autorIa && (
            <div className="absolute -top-3 right-0 flex gap-0.5 bg-white border border-slate-200 rounded shadow-sm p-0.5">
              <button onClick={onEditar} className="p-1 hover:bg-slate-100 rounded" title="Editar"><Edit2 size={11} className="text-slate-600" /></button>
              <button onClick={onRemover} className="p-1 hover:bg-slate-100 rounded" title="Remover"><Trash2 size={11} className="text-red-500" /></button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Renderiza uma imagem do chat. Se for GIF (extensão ou mime), desenha o
 * PRIMEIRO frame num <canvas> em vez de usar <img> — assim o GIF aparece
 * congelado (sem animação). Imagens não-GIF caem no <img> normal.
 *
 * O navegador anima GIFs automaticamente dentro de <img>, mas <canvas>.drawImage
 * só captura o frame que foi pintado uma vez.
 */
function ImagemEstatica({
  url, mime, alt, className, onClick,
}: {
  url: string;
  mime?: string;
  alt: string;
  className?: string;
  onClick?: (e: React.MouseEvent) => void;
}) {
  const ehGif = (mime || "").toLowerCase() === "image/gif" || /\.gif(\?|$)/i.test(url);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (!ehGif) return;
    const img = new window.Image();
    img.onload = () => {
      const c = canvasRef.current;
      if (!c) return;
      c.width = img.naturalWidth;
      c.height = img.naturalHeight;
      const ctx = c.getContext("2d");
      if (ctx) ctx.drawImage(img, 0, 0);
    };
    img.src = url;
  }, [ehGif, url]);

  if (ehGif) {
    return (
      <canvas
        ref={canvasRef}
        aria-label={alt}
        className={className}
        onClick={onClick}
        title={alt}
      />
    );
  }
  return <img src={url} alt={alt} className={className} onClick={onClick} />;
}

function AnexoChat({ a, clarinho }: { a: ChatMensagem["anexos"][number]; clarinho: boolean }) {
  const url = anexoUrlAbsoluto(a.url);
  const Icon = a.tipo === "imagem" ? ImageIcon : a.tipo === "audio" ? Mic : a.tipo === "video" ? Video : FileText;
  const [lightbox, setLightbox] = useState(false);
  const btnDownload = (
    <a
      href={url}
      download={a.nomeOriginal}
      onClick={(e) => e.stopPropagation()}
      className="absolute top-1.5 right-1.5 p-1.5 rounded-full bg-black/60 hover:bg-black/80 text-white opacity-0 group-hover/anexo:opacity-100 transition-opacity"
      title="Baixar"
    >
      <Download size={12} />
    </a>
  );

  if (a.tipo === "imagem") {
    return (
      <>
        <button
          type="button"
          onClick={() => setLightbox(true)}
          className="relative group/anexo block"
          title="Abrir imagem"
        >
          <ImagemEstatica url={url} mime={a.mimeType} alt={a.nomeOriginal} className="max-h-60 rounded-lg object-cover" />
          <span className="absolute inset-0 rounded-lg bg-black/0 group-hover/anexo:bg-black/20 transition-colors flex items-center justify-center">
            <ZoomIn size={20} className="text-white opacity-0 group-hover/anexo:opacity-100 transition-opacity" />
          </span>
          {btnDownload}
        </button>
        {lightbox && <Lightbox url={url} nome={a.nomeOriginal} onClose={() => setLightbox(false)} />}
      </>
    );
  }
  if (a.tipo === "audio") {
    return (
      <div className="min-w-[220px] max-w-[320px] relative group/anexo">
        <AudioWavePlayer url={url} variante={clarinho ? "claro" : "escuro"} />
        <a
          href={url}
          download={a.nomeOriginal}
          className="absolute top-1 right-1 p-1 rounded bg-black/40 hover:bg-black/60 text-white opacity-0 group-hover/anexo:opacity-100 transition-opacity"
          title="Baixar áudio"
        >
          <Download size={11} />
        </a>
        {a.transcricaoStatus === "concluida" && a.transcricao && (
          <p className="text-[10px] italic mt-1 opacity-80">"{a.transcricao}"</p>
        )}
      </div>
    );
  }
  if (a.tipo === "video") {
    return (
      <div className="relative group/anexo">
        <video controls src={url} className="max-h-60 rounded-lg" />
        {btnDownload}
      </div>
    );
  }
  return (
    <div className={`flex items-center gap-2 px-3 py-2 rounded group/anexo ${
      clarinho
        ? "bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-100"
        : "bg-blue-700/40 text-white"
    }`}>
      <a href={url} target="_blank" rel="noreferrer" className="flex items-center gap-2 flex-1 min-w-0 hover:underline">
        <Icon size={14} className="shrink-0" />
        <span className="text-xs truncate">{a.nomeOriginal}</span>
      </a>
      <a
        href={url}
        download={a.nomeOriginal}
        className={`p-1 rounded ${clarinho ? "text-slate-600 hover:bg-slate-200" : "text-white/80 hover:bg-blue-700"}`}
        title="Baixar"
      >
        <Download size={12} />
      </a>
    </div>
  );
}

function Lightbox({ url, nome, onClose }: { url: string; nome: string; onClose: () => void }) {
  useEscape(true, onClose);
  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/85 p-4"
      onClick={onClose}
    >
      <div className="absolute top-4 right-4 flex gap-2">
        <a
          href={url}
          download={nome}
          onClick={(e) => e.stopPropagation()}
          className="p-2.5 rounded-full bg-white/20 hover:bg-white/30 text-white"
          title="Baixar"
        >
          <Download size={18} />
        </a>
        <button
          onClick={onClose}
          className="p-2.5 rounded-full bg-white/20 hover:bg-white/30 text-white"
          title="Fechar (Esc)"
        >
          <X size={18} />
        </button>
      </div>
      <ImagemEstatica
        url={url}
        alt={nome}
        className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      />
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/80 text-xs bg-black/40 px-3 py-1 rounded-full truncate max-w-[80%]">
        {nome}
      </div>
    </div>
  );
}

void MoreVertical; void Bot;
